/**
 * Rótulos de fuso horário traduzidos.
 *
 * `timezones.json` é dado do upstream: a CHAVE é o rótulo em inglês e o VALOR é
 * o identificador IANA — e é o IANA que fica gravado na caixa de entrada. Por
 * isso o arquivo original não é tocado; a tradução mora aqui, indexada pelo
 * rótulo em inglês.
 *
 * Para acrescentar um idioma: crie outra entrada em TIMEZONE_LABELS com o código
 * do idioma. Um idioma sem mapa (ou um fuso sem tradução) cai no rótulo em
 * inglês, nunca em texto vazio.
 *
 * Nomes de cidade que já se escrevem igual em português (Paris, Sydney, Bangkok)
 * não aparecem aqui de propósito — o fallback resolve.
 */

const pt_BR = {
  'International Date Line West': 'Linha Internacional de Data Oeste',
  'Midway Island': 'Ilha Midway',
  'American Samoa': 'Samoa Americana',
  Hawaii: 'Havaí',
  Alaska: 'Alasca',
  'Pacific Time (US & Canada)': 'Hora do Pacífico (EUA e Canadá)',
  'Mountain Time (US & Canada)': 'Hora da Montanha (EUA e Canadá)',
  'Central Time (US & Canada)': 'Hora Central (EUA e Canadá)',
  'Eastern Time (US & Canada)': 'Hora do Leste (EUA e Canadá)',
  'Atlantic Time (Canada)': 'Hora do Atlântico (Canadá)',
  'Central America': 'América Central',
  'Indiana (East)': 'Indiana (Leste)',
  'Mexico City': 'Cidade do México',
  Mazatlan: 'Mazatlán',
  Bogota: 'Bogotá',
  Newfoundland: 'Terra Nova',
  Brasilia: 'Brasília',
  Montevideo: 'Montevidéu',
  'Puerto Rico': 'Porto Rico',
  Greenland: 'Groenlândia',
  'Mid-Atlantic': 'Meio do Atlântico',
  Azores: 'Açores',
  'Cape Verde Is.': 'Ilhas de Cabo Verde',
  Edinburgh: 'Edimburgo',
  Lisbon: 'Lisboa',
  London: 'Londres',
  Monrovia: 'Monróvia',
  Belgrade: 'Belgrado',
  Budapest: 'Budapeste',
  Ljubljana: 'Liubliana',
  Prague: 'Praga',
  Warsaw: 'Varsóvia',
  Brussels: 'Bruxelas',
  Copenhagen: 'Copenhague',
  Madrid: 'Madri',
  Amsterdam: 'Amsterdã',
  Berlin: 'Berlim',
  Bern: 'Berna',
  Zurich: 'Zurique',
  Rome: 'Roma',
  Stockholm: 'Estocolmo',
  Vienna: 'Viena',
  'West Central Africa': 'África Centro-Ocidental',
  Bucharest: 'Bucareste',
  Helsinki: 'Helsinque',
  Sofia: 'Sófia',
  Tallinn: 'Talin',
  Athens: 'Atenas',
  Istanbul: 'Istambul',
  Jerusalem: 'Jerusalém',
  Pretoria: 'Pretória',
  Kaliningrad: 'Kaliningrado',
  Moscow: 'Moscou',
  'St. Petersburg': 'São Petersburgo',
  Volgograd: 'Volgogrado',
  Riyadh: 'Riade',
  Nairobi: 'Nairóbi',
  Baghdad: 'Bagdá',
  Tehran: 'Teerã',
  Muscat: 'Mascate',
  Yerevan: 'Erevan',
  Kabul: 'Cabul',
  Ekaterinburg: 'Ecaterimburgo',
  Islamabad: 'Islamabade',
  Karachi: 'Carachi',
  Kolkata: 'Calcutá',
  'New Delhi': 'Nova Délhi',
  Kathmandu: 'Catmandu',
  Dhaka: 'Daca',
  Rangoon: 'Rangum',
  Hanoi: 'Hanói',
  Jakarta: 'Jacarta',
  Beijing: 'Pequim',
  Singapore: 'Singapura',
  Taipei: 'Taipé',
  Ulaanbaatar: 'Ulã Bator',
  Seoul: 'Seul',
  Tokyo: 'Tóquio',
  Canberra: 'Camberra',
  'Port Moresby': 'Porto Moresby',
  'Solomon Is.': 'Ilhas Salomão',
  'New Caledonia': 'Nova Caledônia',
  'Marshall Is.': 'Ilhas Marshall',
  'Tokelau Is.': 'Ilhas Tokelau',
  'Chatham Is.': 'Ilhas Chatham',
};

export const TIMEZONE_LABELS = { pt_BR };

/**
 * Traduz um rótulo de `timezones.json` mantendo o sufixo de deslocamento.
 * Ex.: 'Brasilia (GMT-03:00)' -> 'Brasília (GMT-03:00)'
 *
 * @param {string} label rótulo original, em inglês
 * @param {string} [locale] idioma da interface
 * @returns {string} rótulo traduzido, ou o original se não houver tradução
 */
export const translateTimezoneLabel = (label, locale) => {
  const dictionary = TIMEZONE_LABELS[locale];
  if (!dictionary || !label) return label;
  // O sufixo '(GMT±HH:MM)' é numérico e não se traduz; separa-se para que o
  // dicionario precise apenas do nome do lugar.
  const match = label.match(/^(.*?)(\s*\([^()]*(?:GMT|UTC)[^()]*\))$/);
  if (!match) return dictionary[label] || label;
  const [, place, offset] = match;
  return `${dictionary[place] || place}${offset}`;
};

export default { TIMEZONE_LABELS, translateTimezoneLabel };
